import { externalConstraint } from '@flatfile/plugin-constraints'
import { recordkeeperListener } from './listeners'
import { recordHook } from '@flatfile/plugin-record-hook'
import {
  formatPlanEin,
  formatPlanSponsor,
  formatPlanName,
  formatPlanIdNumber,
  formatPlanNumber,
  formatZipCode,
  formatDob,
  formatSsn,
  formatCountryCode,
  formatState,
  formatTaxableAmount,
  formatDateOfOriginalDistribution,
  validateDob,
  validateSsn,
  validateEmailAddress,
  validateCountryCodeStateCityZip,
  validateDateOfOriginalDistribution,
} from './participantsDataFormatting'
import { buildTaxableAccountsValidationContext } from './taxableAccountsValidationContext'

export const taxableAccountsParticipantListeners = (listener, userAudience) => {
  const ctx = buildTaxableAccountsValidationContext(userAudience)

  // Formatting: single hook runs all field formatters in sequence
  listener.use(
    recordHook('participant-record', (record) => {
      formatPlanEin(record)
      formatPlanSponsor(record)
      formatPlanName(record)
      formatPlanIdNumber(record)
      formatPlanNumber(record)
      formatZipCode(record)
      formatDob(record)
      formatSsn(record)
      formatCountryCode(record)
      formatState(record)
      formatTaxableAmount(record)
      formatDateOfOriginalDistribution(record)
      return record
    })
  )

  // Validation: single hook runs all validators in sequence
  listener.use(
    recordHook('participant-record', (record) => {
      validateDob(record, ctx)
      validateSsn(record, ctx)
      validateEmailAddress(record)
      validateCountryCodeStateCityZip(record)
      validateDateOfOriginalDistribution(record)
      return record
    })
  )

  listener.use(
    externalConstraint('recordKeeper', (value, key, { record }) => {
      const rules = ctx.recordkeeper
      if (value && rules.noSlash.re.test(value))
        record.addError(key, rules.noSlash.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
    })
  )

  recordkeeperListener(listener, 'participant-record')

  listener.use(
    externalConstraint('tpa', (value, key, { record }) => {
      const rules = ctx.tpa
      if (value && rules.noSlash.re.test(value))
        record.addError(key, rules.noSlash.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (ctx.tpaRequired && value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('planSponsor', (value, key, { record }) => {
      const rules = ctx.planSponsor
      if (value && rules.noSlash.re.test(value))
        record.addError(key, rules.noSlash.error)
      if (value && rules.invalidString.re.test(value))
        record.addError(key, rules.invalidString.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('planName', (value, key, { record }) => {
      const rules = ctx.planName
      if (value && rules.noSlash.re.test(value))
        record.addError(key, rules.noSlash.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (!ctx.isRecordkeeper && value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('planEin', (value, key, { record }) => {
      const rules = ctx.planEin
      if (value && !rules.invalidEINFormat.re.test(value))
        record.addError(key, rules.invalidEINFormat.error)
      if (value && !rules.invalidEIN.re.test(value))
        record.addError(key, rules.invalidEIN.error)
    })
  )

  listener.use(
    externalConstraint('planNumber', (value, key, { record }) => {
      const rules = ctx.planNumber
      if (value && rules.invalidChars.re.test(value))
        record.addError(key, rules.invalidChars.error)
      if (value && !rules.mustBeThreeDigits.re.test(value))
        record.addError(key, rules.mustBeThreeDigits.error)
    })
  )

  listener.use(
    externalConstraint('planIdNumber', (value, key, { record }) => {
      const { maxChar } = ctx.planIdNumber
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('firstName', (value, key, { record }) => {
      const rules = ctx.firstName
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.namePrefix.re.test(value))
        record.addError(key, rules.namePrefix.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
      if (value && rules.allowedNameCharacters.re.test(value))
        record.addError(key, rules.allowedNameCharacters.error)
    })
  )

  listener.use(
    externalConstraint('middleName', (value, key, { record }) => {
      const rules = ctx.middleName
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.allowedNameCharacters.re.test(value))
        record.addError(key, rules.allowedNameCharacters.error)
    })
  )

  listener.use(
    externalConstraint('lastName', (value, key, { record }) => {
      const rules = ctx.lastName
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (value && rules.allowedNameCharacters.re.test(value))
        record.addError(key, rules.allowedNameCharacters.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('addressLine1', (value, key, { record }) => {
      const rules = ctx.addressLine1
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (value && !rules.mustContainNumberAndLetter.re.test(value))
        record.addError(key, rules.mustContainNumberAndLetter.error)
    })
  )

  listener.use(
    externalConstraint('addressLine2', (value, key, { record }) => {
      const { maxChar } = ctx.addressLine2
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('city', (value, key, { record }) => {
      const { maxChar } = ctx.city
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('zipCode', (value, key, { record }) => {
      const { maxChar } = ctx.zipCode
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('countryCode', (value, key, { record }) => {
      const { maxChar } = ctx.countryCode
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('ssn', (value, key, { record }) => {
      const { validityCheck } = ctx.ssn
      if (value && validityCheck.re.test(value))
        record.addError(key, validityCheck.error)
    })
  )

  listener.use(
    externalConstraint('emailAddress', (value, key, { record }) => {
      const { maxChar } = ctx.emailAddress
      if (value && maxChar.re.test(value)) record.addError(key, maxChar.error)
    })
  )

  listener.use(
    externalConstraint('taxableAmount', (value, key, { record }) => {
      const rules = ctx.taxableAmount
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value) {
        const numeric = Number.parseFloat(value.replaceAll(/[^0-9.-]/g, ''))
        if (Number.isNaN(numeric) || numeric <= 0)
          record.addError(key, rules.invalidAmount.error)
      }
    })
  )
}
